import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useSlaveEvaluations from '@/data/hook/evaluation/useSlaveEvaluations';
import {
  applyTemplate,
  updateEvaluation,
} from '@/data/services/evaluationServices';
import Evaluation from '@/models/Evaluation';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';

type UseCoursePathEvaluationProps = {
  coursePathId: number;
  courseLineId: number;
  masters: Evaluation[];
};

type UpdateEvaluationArgs = {
  evaluationId: number;
  data: Partial<Evaluation>;
};

const useCoursePathEvaluation = ({
  coursePathId,
  courseLineId,
  masters,
}: UseCoursePathEvaluationProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab.coursePath',
  });
  const { t: tBlock } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const { slaves, isLoading, cacheManager } = useSlaveEvaluations({
    courseLineId,
    masters,
  });

  const evaluation = slaves.find(s => s.coursePathId === coursePathId);

  const applyMutation = useMutation({
    mutationFn: (templateId: number) =>
      applyTemplate({ templateId, coursePathId }),
    onSuccess: newSlave => {
      const replaced = slaves.find(s => s.coursePathId === coursePathId);
      cacheManager.batch([
        ...(replaced ? [() => cacheManager.remove(replaced.id)] : []),
        () => cacheManager.add(newSlave),
      ]);
      alert.success(t('applyTemplateSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ evaluationId, data }: UpdateEvaluationArgs) =>
      updateEvaluation(evaluationId, data),
    onSuccess: updated => {
      cacheManager.update(updated);
      alert.success(tBlock('updateBlockSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  return {
    evaluation,
    isLoading,
    selectTemplate: applyMutation.mutateAsync,
    updateEvaluation: updateMutation.mutateAsync,
    isMutating: applyMutation.isPending || updateMutation.isPending,
    cacheManager,
  };
};

export default useCoursePathEvaluation;
